import type {
  Attachment,
  DiagnosticReport,
  Observation,
  Resource,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'
import { useParams } from 'react-router-dom'
import { parsePatientChartSplat } from '../patientChartPath'
import { useMedplum } from '@medplum/react'
import { formatRange } from '@medplum/core'
import { useEffect, useState, useRef } from 'react'
import { formatDateTime } from '@/utils.ts'
import { Loader, Table } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { Markable } from '@/components'
import { useScrollContext } from '@/context/ScrollContext'
import { PdfViewer } from '@/components/PdfViewer'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { tiffToURL } from '@/utils/tiffToURL'
import { useAuditRecordOpenReporting } from '@/pages/Patient/useAuditDocumentReporting'

type Props = {
  sourceResourceId?: string
}

export const LabResults = ({ sourceResourceId }: Props) => {
  const params = useParams()
  const medplum = useMedplum()
  const { scrollToTop, scrollContainerId } = useScrollContext()

  const [resource, setResource] = useState<DiagnosticReport>()
  const [services, setServices] = useState<ServiceRequest[]>([])
  const [result, setResult] = useState<Observation[]>([])
  const [abnormalCount, setAbnormalCount] = useState(0)
  const [resourceId, setResourceId] = useState<string | undefined>(sourceResourceId)
  const [isNotFound, setIsNotFound] = useState(false)

  const ref = useRef(null)

  // QA round 3 — opening a lab/imaging report is a RECORD open, reported whether or not
  // a presentedForm attachment exists (round 1 keyed this on the attachment, so a
  // result-only report logged nothing). The server reads the DiagnosticReport and names
  // the event with the lab's own name under the labs-imaging section.
  //
  // QA round 3 (re-test) — keyed on the id the reader NAVIGATED TO, not on the fetched
  // resource. Keying it on `resource?.id` made the record of an access conditional on that
  // access having succeeded, so a report that failed to load — the case a compliance reader
  // most wants to see — recorded nothing. The server reads the record itself either way.
  useAuditRecordOpenReporting(
    params.patientId,
    resourceId !== undefined ? { resourceType: 'DiagnosticReport', id: resourceId } : undefined,
  )

  useEffect(() => {
    if (sourceResourceId) {
      setResourceId(sourceResourceId)
      return
    }
    const parsedResourceId = parsePatientChartSplat(params['*']).resourceId

    if (parsedResourceId !== resourceId) {
      setResourceId(parsedResourceId)
    }
  }, [params, resourceId, sourceResourceId])

  useEffect(() => {
    scrollToTop()
  }, [resourceId, scrollToTop])

  useEffect(() => {
    if (resourceId) {
      medplum
        .search(
          'DiagnosticReport',
          `_id=${resourceId}&_include=DiagnosticReport:based-on&_include=DiagnosticReport:result`,
        )
        .then(async (resources) => {
          const neededResource = resources.entry?.find(
            (resource) => resource.resource?.id === resourceId,
          )?.resource as DiagnosticReport | undefined

          const serviceRequests =
            resources.entry
              ?.filter((item) => (item.resource as Resource)?.resourceType === 'ServiceRequest')
              ?.map((item) => item.resource) || []
          setServices(serviceRequests as unknown as ServiceRequest[])

          const observations =
            resources.entry
              ?.filter((item) => (item.resource as Resource)?.resourceType === 'Observation')
              ?.map((item) => item.resource) || []

          const memberRefs = observations
            .flatMap(
              (obs) =>
                (obs as unknown as Observation).hasMember?.map((member) =>
                  member.reference?.split('/').pop(),
                ) ?? [],
            )
            .filter((id): id is string => !!id)

          if (memberRefs.length) {
            const memberResources = await medplum.executeBatch({
              resourceType: 'Bundle',
              type: 'batch',
              entry: memberRefs.map((id) => ({
                request: {
                  method: 'GET',
                  url: `Observation/${id}`,
                },
              })),
            })

            const memberResourceMap = new Map(
              memberResources.entry?.map((entry) => [entry.resource?.id, entry.resource]),
            )

            observations.forEach((obs) => {
              const observation = obs as unknown as Observation
              if (observation.hasMember?.length) {
                observation.hasMember.forEach((member, index) => {
                  const id = member.reference?.split('/').pop()
                  if (id && observation.hasMember?.[index]) {
                    observation.hasMember[index].resource = memberResourceMap.get(id) as Observation
                  }
                })
              }
            })
          }

          setResult(observations as unknown as Observation[])

          const abnormalCount = (observations as unknown as Observation[]).reduce((count, obs) => {
            if (obs.hasMember?.length) {
              return (
                count +
                obs.hasMember.filter(
                  (member) =>
                    (member.resource as Observation)?.interpretation?.[0]?.coding?.[0]?.display
                      ?.toLowerCase()
                      .includes('high') ||
                    (member.resource as Observation)?.interpretation?.[0]?.coding?.[0]?.display
                      ?.toLowerCase()
                      .includes('low') ||
                    (member.resource as Observation)?.interpretation?.[0]?.coding?.[0]?.display
                      ?.toLowerCase()
                      .includes('abnormal'),
                ).length
              )
            }
            return (
              count +
              (obs.interpretation?.[0]?.coding?.[0]?.display?.toLowerCase().includes('high') ||
              obs.interpretation?.[0]?.coding?.[0]?.display?.toLowerCase().includes('low')
                ? 1
                : 0)
            )
          }, 0)

          setAbnormalCount(abnormalCount)
          if (neededResource) {
            setResource(neededResource)
            setIsNotFound(false)
          }
        })
        .catch((e) => {
          setIsNotFound(true)
          logError('patient', 'Failed to fetch lab results', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resource',
            color: 'red',
          })
        })
    }

    return () => {
      setResult([])
      setServices([])
      setResource(undefined)
      setAbnormalCount(0)
    }
  }, [medplum, resourceId])

  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const getDoctor = () =>
    services[0]?.extension?.find((e) => e.url?.includes('servicerequest-authorizedBy'))
      ?.valueReference?.display || ''

  const getServiceReqByPanel = (panel: Observation) =>
    services.find((sr) => sr.code?.text === panel.code?.text)

  function scrollToPanel(id: string) {
    const element = panelRefs.current[id]
    if (!element) return

    const yOffset = -150

    if (scrollContainerId) {
      const container = document.getElementById(scrollContainerId)
      if (container) {
        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const top = elementRect.top - containerRect.top + container.scrollTop + yOffset

        container.scrollTo({ top, behavior: 'smooth' })
        return
      }
    }

    // Without a scroll-context id the scrolling element is the chart pane's
    // inner overflow container, not the window — scrollIntoView finds it.
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (!resourceId || isNotFound) {
    return null
  }

  if (!resource) {
    return (
      <div
        style={{ width: 'calc(100% - 560px - var(--zoom-panel-width, 0px))' }}
        className="fixed left-[560px] flex h-full items-center justify-center gap-3 text-sm text-[#7D7987]"
      >
        <Loader size="sm" />
        Loading lab results...
      </div>
    )
  }

  return (
    <div className="relative box-border p-2 whitespace-pre-wrap" ref={ref}>
      {/*<div className="whitespace-pre-wrap">{JSON.stringify(resource, null, 2)}</div>*/}
      <div className="w-full bg-white pt-2">
        <p className="mb-4 text-2xl font-bold">{resource.performer?.[0]?.display}</p>
        <div className="flex justify-between gap-8">
          <div className="flex flex-1 items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Source:</p>
              <p className="text-sm font-bold text-[#7D7987]">Total Abnormal Results:</p>
              <p className="text-sm font-bold text-[#7D7987]">Accession Number:</p>
              <p className="text-sm font-bold text-[#7D7987]">Date of Report:</p>
              <p className="text-sm font-bold text-[#7D7987]">Referring Doctor:</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">{resource.performer?.[0]?.display}</p>
              <p className="text-base text-sm">{abnormalCount}</p>
              <p className="text-base text-sm">
                {resource.identifier?.find((i) => i.type?.coding?.[0]?.code === 'ACSN')?.value ||
                  '-'}
              </p>
              <p className="text-base text-sm">
                {resource.issued ? formatDateTime(resource.issued, '-', true) : '-'}
              </p>
              <p className="text-base text-sm">{getDoctor() || '-'}</p>
            </div>
          </div>

          <div className="flex flex-1 items-start gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-[#7D7987]">Result Received:</p>
              <p className="text-sm font-bold text-[#7D7987]">Result Modified:</p>
              <p className="text-sm font-bold text-[#7D7987]">Lab Ref #:</p>
              <p className="text-sm font-bold text-[#7D7987]">Status:</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-base text-sm">
                {resource.issued ? formatDateTime(resource.issued, '-', true) : '-'}
              </p>
              <p className="text-base text-sm">
                {resource.meta?.lastUpdated
                  ? formatDateTime(resource.meta?.lastUpdated, '-', true)
                  : '-'}
              </p>
              <p className="text-base text-sm">
                {resource.identifier?.find((i) => i.type?.coding?.[0]?.code === 'PLAC')?.value ||
                  '-'}
              </p>
              <p className="text-base text-sm">{resource.status || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* PD-1159: ToC scrolls with the content — must not be sticky. */}
      <div>
        <div className="my-4 h-[1px] bg-gray-200" />

        <p className="mb-2 text-sm font-bold text-[#7D7987]">Table of content</p>
        <div className="mb-4 flex flex-wrap gap-5 pb-1 text-sm font-medium text-[#3C6ADD]">
          {result.map((panel) => (
            <button
              key={panel.id}
              className="cursor-pointer hover:underline focus:underline"
              onClick={() => scrollToPanel(panel.id!)}
              type="button"
            >
              {panel.code.text || panel.code.coding?.[0]?.display}
            </button>
          ))}
        </div>
        <div className="my-4 h-[1px] bg-gray-200" />
      </div>

      {result.map((panel) => {
        const sr = getServiceReqByPanel(panel)
        const nonOrdinaryMembers = panel.hasMember
          ?.filter((item) => !(item.resource as Observation)?.valueQuantity)
          .map((item) => item.resource as Observation)
          .filter(Boolean)

        const nonOrdinaryIds: string[] = nonOrdinaryMembers?.map((item) => item?.id || '') || []
        const restMembers = panel.hasMember?.filter(
          (item) => item.resource?.id && !nonOrdinaryIds.includes(item.resource?.id),
        )

        return (
          <div
            key={panel.id}
            ref={(el: HTMLDivElement | null) => {
              if (panel.id) {
                panelRefs.current[panel.id] = el
              }
            }}
            className="mb-8"
          >
            <div className="text-md mb-1 text-base font-bold">
              {panel.code.text || panel.code.coding?.[0]?.display}
            </div>
            <div className="mb-2 flex gap-4 text-xs">
              {sr?.authoredOn && (
                <span>
                  <span className="text-[#7D7987]">Collected:</span>{' '}
                  {formatDateTime(sr.authoredOn, '-', true)}
                </span>
              )}
              {panel.effectiveDateTime && (
                <span>
                  <span className="text-[#7D7987]">Received:</span>{' '}
                  {formatDateTime(panel.effectiveDateTime, '-', true)}
                </span>
              )}
              {panel.issued && (
                <span>
                  <span className="text-[#7D7987]">Reported:</span>{' '}
                  {formatDateTime(panel.issued, '-', true)}
                </span>
              )}
            </div>
            {panel.note?.[0]?.text && (
              <span
                className="text-base text-sm"
                dangerouslySetInnerHTML={{ __html: panel.note[0].text }}
              />
            )}

            {!!restMembers?.length && (
              <Table striped>
                <Table.Tbody>
                  <Table.Tr className="text-[#7D7987]">
                    <Table.Th style={{ width: '40%' }}>Test</Table.Th>
                    <Table.Th style={{ width: '20%' }}>Value</Table.Th>
                    <Table.Th style={{ width: '20%' }}>Interpretation</Table.Th>
                  </Table.Tr>

                  {restMembers.map((member) =>
                    member.resource ? (
                      <TableRow observation={member.resource as Observation} />
                    ) : null,
                  )}

                  {!panel.hasMember?.length && <TableRow observation={panel} />}
                </Table.Tbody>
              </Table>
            )}

            {!!nonOrdinaryMembers?.length &&
              nonOrdinaryMembers?.map((member) => {
                const valueString = member.valueString
                const notes = member.note?.map((i) => i.text).join('\n')

                const displays: string[] = []

                if (member.code?.text) {
                  displays.push(member.code.text)
                }

                if (valueString && !notes) {
                  displays.push(valueString)
                }

                if (notes && !valueString) {
                  displays.push(notes)
                }

                if (notes && valueString && notes !== valueString) {
                  displays.push(notes)
                  displays.push(valueString)
                }

                if (notes && valueString && notes === valueString) {
                  displays.push(notes)
                }

                return displays.map((display) => <Markable>{display}</Markable>)
              })}
          </div>
        )
      })}

      {resource.presentedForm?.length && (
        <div className="mt-6">
          <p className="text-xl font-bold">Attachments</p>
          {resource.presentedForm?.map((attachment, index) => (
            <PresentedFormAttachment
              key={`${attachment.url || attachment.title || 'attachment'}-${index}`}
              attachment={attachment}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PresentedFormAttachment({ attachment, index }: { attachment: Attachment; index: number }) {
  const contentType = attachment.contentType || ''
  const title = attachment.title || `Attachment ${index + 1}`
  const attachmentUrl = getPresentedFormAttachmentUrl(attachment)

  if (!attachmentUrl) {
    return (
      <div className="mt-4">
        <p className="mb-2 text-sm text-gray-700">{title}</p>
        <p className="text-sm text-red-500">Attachment is unavailable.</p>
      </div>
    )
  }

  if (/pdf/i.test(contentType)) {
    return (
      <div className="mt-4">
        <p className="mb-2 text-sm text-gray-700">{title}</p>
        <PdfViewer fileUrl={attachmentUrl} />
      </div>
    )
  }

  if (/tiff?/i.test(contentType)) {
    return <TiffAttachmentPreview attachmentUrl={attachmentUrl} title={title} />
  }

  if (/^image\//i.test(contentType)) {
    return <ImageAttachmentPreview attachmentUrl={attachmentUrl} title={title} />
  }

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm text-gray-700">{title}</p>
      <a
        className="text-blue-600 underline"
        href={attachmentUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open attachment
      </a>
    </div>
  )
}

function getPresentedFormAttachmentUrl(attachment: Attachment): string | undefined {
  if (attachment.url) {
    return attachment.url
  }

  if (!attachment.data) {
    return undefined
  }

  return `data:${attachment.contentType || 'application/octet-stream'};base64,${attachment.data}`
}

function ImageAttachmentPreview({
  attachmentUrl,
  title,
}: {
  attachmentUrl: string
  title: string
}) {
  const medplum = useMedplum()
  const [objectUrl, setObjectUrl] = useState<string>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let createdUrl: string | undefined
    setObjectUrl(undefined)
    setFailed(false)

    downloadAttachment(medplum, attachmentUrl)
      .then((blob) => {
        if (cancelled) {
          return
        }
        createdUrl = URL.createObjectURL(blob)
        setObjectUrl(createdUrl)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        logError('patient', 'Failed to load lab image attachment', err)
        setFailed(true)
      })

    return () => {
      cancelled = true
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl)
      }
    }
  }, [attachmentUrl, medplum])

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm text-gray-700">{title}</p>
      {failed && <p className="text-sm text-red-500">Failed to load image attachment.</p>}
      {!failed && !objectUrl && <p className="text-sm text-gray-500">Loading image...</p>}
      {objectUrl && (
        <img
          src={objectUrl}
          alt={title}
          className="block h-auto max-w-full border border-gray-100 bg-white object-contain"
        />
      )}
    </div>
  )
}

function TiffAttachmentPreview({ attachmentUrl, title }: { attachmentUrl: string; title: string }) {
  const medplum = useMedplum()
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [failed, setFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    let createdUrls: string[] = []
    setImageUrls([])
    setFailed(false)
    setLoaded(false)

    tiffToURL(medplum, attachmentUrl)
      .then((urls) => {
        if (cancelled) {
          urls.forEach((url) => URL.revokeObjectURL(url))
          return
        }
        createdUrls = urls
        setImageUrls(urls)
        setLoaded(true)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        logError('patient', 'Failed to load lab TIFF attachment', err)
        setFailed(true)
      })

    return () => {
      cancelled = true
      createdUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [attachmentUrl, medplum])

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm text-gray-700">{title}</p>
      {failed && <p className="text-sm text-red-500">Failed to load TIFF attachment.</p>}
      {!failed && !loaded && <p className="text-sm text-gray-500">Loading image...</p>}
      {!failed && loaded && !imageUrls.length && (
        <p className="text-sm text-gray-500">No previewable TIFF pages found.</p>
      )}
      <div className="flex flex-col gap-3">
        {imageUrls.map((url, pageIndex) => (
          <img
            key={url}
            src={url}
            alt={`${title} page ${pageIndex + 1}`}
            className="block h-auto max-w-full border border-gray-100 bg-white object-contain"
          />
        ))}
      </div>
    </div>
  )
}

function TableRow({ observation }: { observation: Observation }) {
  const interp = observation.interpretation?.[0]?.coding?.[0]
  const interpDisplay = interp?.display
  // coloring abnormal results
  const isAbnormal =
    interpDisplay?.toLowerCase().includes('high') ||
    interpDisplay?.toLowerCase().includes('low') ||
    interpDisplay?.toLowerCase().includes('abnormal')

  const value = observation.valueQuantity?.value || observation.valueCodeableConcept?.text
  const unit =
    observation.valueQuantity?.unit || observation.valueCodeableConcept?.coding?.[0]?.display || ''

  const range = observation.referenceRange?.[0]
    ? `(${formatRange(observation.referenceRange[0])} ${unit})`
    : ''

  return (
    <Table.Tr key={observation.id} className="text-sm">
      <Table.Td className="py-1 pl-1 text-gray-700" style={{ width: '40%' }}>
        {observation.code.text || observation.code.coding?.[0]?.display} {range}
        <Markable>{observation.note?.map((note) => note.text).join('\n') || ''}</Markable>
      </Table.Td>
      <Table.Td className="py-1 pr-2 align-top text-black" style={{ width: '20%' }}>
        {value}
      </Table.Td>
      <Table.Td className="py-1 pr-2 align-top" style={{ width: '20%' }}>
        {interpDisplay && (
          <span className={isAbnormal ? 'font-semibold text-red-500' : 'text-gray-400'}>
            {interpDisplay === 'Above high normal'
              ? '▲'
              : interpDisplay === 'Below low normal'
                ? '▼'
                : ''}
            {interpDisplay}
          </span>
        )}
      </Table.Td>
    </Table.Tr>
  )
}
